import "./OfferStack.scss";
import PropTypes from "prop-types";
import { useEffect, useLayoutEffect, useRef } from "react";

const SCALE_STEP = 0.9;
const MIN_SCALE = 0.66;
const DIM_STEP = 0.12;
const MAX_DIM = 0.5;
/** Share of the remaining distance covered each frame; lower is softer. */
const EASE = 0.12;

/** Example offer cards in a stack; the stack follows the cursor, bringing the nearest card forward. */
function OfferStack({ cards, dealerLabel, cta, label }) {
	const resting = (cards.length - 1) / 2;
	const cardRefs = useRef([]);
	const target = useRef(resting);
	const current = useRef(resting);
	const frame = useRef(0);

	const paint = () => {
		cardRefs.current.forEach((el, i) => {
			if (!el) return;
			const distance = Math.abs(i - current.current);
			el.style.setProperty("--scale", Math.max(MIN_SCALE, SCALE_STEP ** distance));
			el.style.setProperty("--dim", Math.min(MAX_DIM, distance * DIM_STEP));
			el.style.zIndex = String(Math.round(1000 - distance * 100));
		});
	};

	const tick = () => {
		const gap = target.current - current.current;
		if (Math.abs(gap) < 0.001) {
			current.current = target.current;
			paint();
			frame.current = 0;
			return;
		}
		current.current += gap * EASE;
		paint();
		frame.current = requestAnimationFrame(tick);
	};

	const moveTo = (position) => {
		target.current = position;
		if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
			current.current = position;
			paint();
			return;
		}
		if (!frame.current) frame.current = requestAnimationFrame(tick);
	};

	useLayoutEffect(paint);
	useEffect(() => () => cancelAnimationFrame(frame.current), []);

	const handlePointerMove = (e) => {
		if (e.pointerType !== "mouse") return;
		const rect = e.currentTarget.getBoundingClientRect();
		const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
		moveTo(ratio * (cards.length - 1));
	};

	return (
		<div
			className="offer-stack"
			role="img"
			aria-label={label}
			onPointerMove={handlePointerMove}
			onPointerLeave={() => moveTo(resting)}
			style={{ "--count": cards.length }}>
			{cards.map((card, i) => (
				<div
					className="offer-stack__card"
					key={card.model}
					ref={(el) => {
						cardRefs.current[i] = el;
					}}
					aria-hidden="true"
					style={{ "--i": i }}>
					<img className="offer-stack__photo" src={card.image} alt="" loading="lazy" />
					<div className="offer-stack__top">
						<img className="offer-stack__logo" src={card.logo} alt="" />
						<span className="offer-stack__dealer">{dealerLabel}</span>
					</div>
					<div className="offer-stack__body">
						<p className="offer-stack__model">{card.model}</p>
						<p className="offer-stack__price">
							{card.price}
							<span> /mo</span>
						</p>
						<p className="offer-stack__das">{card.das}</p>
						<span className="offer-stack__cta">{cta}</span>
						<p className="offer-stack__ends">{card.ends}</p>
					</div>
				</div>
			))}
		</div>
	);
}

OfferStack.propTypes = {
	cards: PropTypes.arrayOf(
		PropTypes.shape({
			image: PropTypes.string.isRequired,
			logo: PropTypes.string.isRequired,
			model: PropTypes.string.isRequired,
			price: PropTypes.string.isRequired,
			das: PropTypes.string.isRequired,
			ends: PropTypes.string.isRequired,
		}),
	).isRequired,
	dealerLabel: PropTypes.string.isRequired,
	cta: PropTypes.string.isRequired,
	label: PropTypes.string.isRequired,
};

export default OfferStack;
